import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { User as UserIcon, Lock } from "lucide-react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  React.useEffect(() => { if (user === false) nav("/login", { state: { from: "/profile" } }); }, [user, nav]);
  if (!user) return null;

  return (
    <div className="mx-auto max-w-3xl px-6 lg:px-8 py-12 space-y-6" data-testid="profile-page">
      <Seo title="Profile — BizzAtlas" description="Manage your BizzAtlas account and password." />
      <Breadcrumbs items={[{ label: "BizzAtlas", to: "/" }, { label: "Dashboard", to: "/dashboard" }, { label: "Profile" }]} />
      <div>
        <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Account</div>
        <h1 className="font-display text-4xl tracking-tight">Profile</h1>
      </div>

      <ProfileForm user={user} onSaved={refresh} />
      <ChangePasswordForm />
    </div>
  );
}

function ProfileForm({ user, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      if (name !== user.name) payload.name = name;
      if (email.toLowerCase() !== user.email) payload.email = email;
      if (Object.keys(payload).length === 0) { toast.info("No changes to save"); return; }
      await api.patch("/auth/profile", payload);
      toast.success("Profile updated");
      if (payload.email) toast.message("Verify your new email via the link we sent.");
      onSaved && (await onSaved());
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="font-display flex items-center gap-2"><UserIcon className="h-5 w-5" />Profile details</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label htmlFor="pf-name">Name</Label><Input id="pf-name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></div>
            <div className="space-y-2"><Label htmlFor="pf-email">Email</Label><Input id="pf-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-email" /></div>
          </div>
          <div className="text-xs text-zinc-500">Role: <b className="capitalize">{user.role}</b> · Plan: <b className="capitalize">{user.membership}</b> · Ad credits: <b>{user.ad_credits}</b></div>
          <Button type="submit" disabled={busy} className="btn-brand" data-testid="profile-save">{busy ? "Saving…" : "Save changes"}</Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ChangePasswordForm() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password changed");
      setCur(""); setNext("");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Change failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="font-display flex items-center gap-2"><Lock className="h-5 w-5" />Change password</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4 max-w-md">
          <div className="space-y-2"><Label htmlFor="pf-cur">Current password</Label><Input id="pf-cur" type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="profile-cur-pw" /></div>
          <div className="space-y-2"><Label htmlFor="pf-next">New password</Label><Input id="pf-next" type="password" required minLength={6} value={next} onChange={(e) => setNext(e.target.value)} data-testid="profile-new-pw" /></div>
          <Button type="submit" disabled={busy || !cur || !next} className="btn-brand" data-testid="profile-change-pw">{busy ? "Updating…" : "Update password"}</Button>
        </form>
      </CardContent>
    </Card>
  );
}
